import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Mail, Smartphone } from 'lucide-react';
import { auth } from '@/firebase';
import { trackAppStoreTap, trackNavigationClick } from '@/utils/Analytics';
import { appStoreLink } from '@/config/appStoreLink';
import { appHref } from '@/utils/appHref';

/**
 * The states the footer links directly. Virginia plus the four highest-demand
 * checked jurisdictions; every other state is one click away behind "All states".
 */
const FOOTER_LICENSING_STATES = [
  { abbr: 'va', name: 'Virginia' },
  { abbr: 'ca', name: 'California' },
  { abbr: 'tx', name: 'Texas' },
  { abbr: 'ny', name: 'New York' },
  { abbr: 'fl', name: 'Florida' },
] as const;

const Footer: React.FC = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setIsLoggedIn(!!user);
    });

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);

  return (
    <footer className="bg-neutral-900 text-neutral-100 mt-auto">
      <div className="border-t border-neutral-800"></div>

      <div className="container-base py-12">
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-5 gap-x-6 gap-y-8 md:gap-8 mb-8">
          {/* Brand Section */}
          <div className="md:col-span-2 animate-fade-in-up opacity-0">
            <h3 className="font-display text-2xl font-semibold text-neutral-100 mb-3">
              Social Work Test Prep Academy
            </h3>
            <p className="body-base text-neutral-400 max-w-md mb-4">
              Professional ASWB exam preparation with adaptive learning technology.
              Pass your licensing exam with confidence.
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.facebook.com/profile.php?id=61565921293583"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackNavigationClick('social_media', 'Facebook', 'https://www.facebook.com/profile.php?id=61565921293583', isLoggedIn)}
                className="p-2 rounded-lg bg-neutral-800 text-neutral-400 hover:text-neutral-900 hover:bg-primary-600 transition-all duration-200"
                aria-label="Facebook"
              >
                <Facebook size={20} />
              </a>
              <a
                href="https://www.instagram.com/socialworktestprepacademy?igsh=MTUyYzQ1MGI0Ymg1ZA%3D%3D&utm_source=qr"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackNavigationClick('social_media', 'Instagram', 'https://www.instagram.com/socialworktestprepacademy', isLoggedIn)}
                className="p-2 rounded-lg bg-neutral-800 text-neutral-400 hover:text-neutral-900 hover:bg-primary-600 transition-all duration-200"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="animate-fade-in-up opacity-0 delay-100">
            <h4 className="heading-3 text-neutral-100 mb-4">Quick Links</h4>
            <nav className="flex flex-col gap-2">
              <a
                href={appStoreLink('footer')}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  trackAppStoreTap('footer');
                  trackNavigationClick('footer_link', 'iPhone app', appStoreLink('footer'), isLoggedIn);
                }}
                className="body-base text-neutral-400 hover:text-neutral-900 transition-colors duration-200 flex items-center gap-2"
              >
                <Smartphone size={16} />
                iPhone app
              </a>
              <Link
                to="/blog"
                onClick={() => trackNavigationClick('footer_link', 'Blog', '/blog', isLoggedIn)}
                className="body-base text-neutral-400 hover:text-neutral-900 transition-colors duration-200"
              >
                Blog
              </Link>
              <Link
                to="/about"
                onClick={() => trackNavigationClick('footer_link', 'About Us', '/about', isLoggedIn)}
                className="body-base text-neutral-400 hover:text-neutral-900 transition-colors duration-200"
              >
                About Us
              </Link>
              <Link
                to="/pricing"
                onClick={() => trackNavigationClick('footer_link', 'Pricing', '/pricing', isLoggedIn)}
                className="body-base text-neutral-400 hover:text-neutral-900 transition-colors duration-200"
              >
                Pricing
              </Link>
              <Link
                to="/affiliate/signup"
                onClick={() => trackNavigationClick('footer_link', 'Become an Affiliate', '/affiliate/signup', isLoggedIn)}
                className="body-base text-neutral-400 hover:text-neutral-900 transition-colors duration-200"
              >
                Become an Affiliate
              </Link>
              {isLoggedIn && (
                <>
                  <Link
                    to="/feedback"
                    onClick={() => trackNavigationClick('footer_link', 'Feedback', '/feedback', isLoggedIn)}
                    className="body-base text-neutral-400 hover:text-neutral-900 transition-colors duration-200"
                  >
                    Feedback
                  </Link>
                  <Link
                    to="/settings"
                    onClick={() => trackNavigationClick('footer_link', 'Settings', '/settings', isLoggedIn)}
                    className="body-base text-neutral-400 hover:text-neutral-900 transition-colors duration-200"
                  >
                    Settings
                  </Link>
                </>
              )}
            </nav>
          </div>

          {/* Licensing — the public state pages (#418). They answer the question
              people ask before they buy any prep, so they get a persistent route
              in from every page rather than living only on /licensing itself. */}
          <div className="animate-fade-in-up opacity-0 delay-100">
            <h4 className="heading-3 text-neutral-100 mb-4">Licensing</h4>
            <nav className="flex flex-col gap-2">
              <Link
                to="/licensing"
                onClick={() => trackNavigationClick('footer_link', 'All states', '/licensing', isLoggedIn)}
                className="body-base text-neutral-400 hover:text-neutral-900 transition-colors duration-200"
              >
                All states
              </Link>
              <Link
                to="/programs"
                onClick={() => trackNavigationClick('footer_link', 'Programs', '/programs', isLoggedIn)}
                className="body-base text-neutral-400 hover:text-neutral-900 transition-colors duration-200"
              >
                Programs
              </Link>
              {/* The five state links are md+ only — on small screens the
                  Licensing column shows just "All states" + the JSON link so
                  four stacked footer groups can go two-up without one column
                  towering over the rest. `md:contents` un-boxes the wrapper at
                  md+ so the desktop layout (five flex children in this nav) is
                  byte-for-byte what it was before. */}
              <div className="hidden md:contents">
                {FOOTER_LICENSING_STATES.map(({ abbr, name }) => (
                  <Link
                    key={abbr}
                    to={`/licensing/${abbr}`}
                    onClick={() => trackNavigationClick('footer_link', name, `/licensing/${abbr}`, isLoggedIn)}
                    className="body-base text-neutral-400 hover:text-neutral-900 transition-colors duration-200"
                  >
                    {name}
                  </Link>
                ))}
              </div>
              {/* Plain <a>: the JSON is a static build artifact, not a route. */}
              <a
                href={appHref('/licensing/index.json')}
                onClick={() => trackNavigationClick('footer_link', 'Licensing data (JSON)', '/licensing/index.json', isLoggedIn)}
                className="body-base text-neutral-400 hover:text-neutral-900 transition-colors duration-200"
              >
                Licensing data (JSON)
              </a>
            </nav>
          </div>

          {/* Legal & Contact */}
          <div className="animate-fade-in-up opacity-0 delay-200">
            <h4 className="heading-3 text-neutral-100 mb-4">Legal & Support</h4>
            <nav className="flex flex-col gap-2">
              <Link
                to="/privacy-policy"
                onClick={() => trackNavigationClick('footer_link', 'Privacy Policy', '/privacy-policy', isLoggedIn)}
                className="body-base text-neutral-400 hover:text-neutral-900 transition-colors duration-200"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                onClick={() => trackNavigationClick('footer_link', 'Terms of Service', '/terms', isLoggedIn)}
                className="body-base text-neutral-400 hover:text-neutral-900 transition-colors duration-200"
              >
                Terms of Service
              </Link>
              <Link
                to="/disclosures"
                onClick={() => trackNavigationClick('footer_link', 'Disclosures', '/disclosures', isLoggedIn)}
                className="body-base text-neutral-400 hover:text-neutral-900 transition-colors duration-200"
              >
                Disclosures
              </Link>
              {/* Anchors the CPRA opt-out section of the policy. Required to be
                  reachable from every page, not buried in the document. */}
              <Link
                to="/privacy-policy#do-not-sell"
                onClick={() => trackNavigationClick('footer_link', 'Do Not Sell or Share', '/privacy-policy#do-not-sell', isLoggedIn)}
                className="body-base text-neutral-400 hover:text-neutral-900 transition-colors duration-200"
              >
                Do Not Sell or Share My Personal Information
              </Link>
              <a
                href="mailto:support@socialworktestprepacademy.com"
                onClick={() => trackNavigationClick('footer_link', 'Contact Us', 'mailto:support@socialworktestprepacademy.com', isLoggedIn)}
                className="body-base text-neutral-400 hover:text-neutral-900 transition-colors duration-200 flex items-center gap-2"
              >
                <Mail size={16} />
                Contact Us
              </a>
            </nav>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-neutral-800 animate-fade-in-up opacity-0 delay-300">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="body-small text-neutral-500">
              &copy; {new Date().getFullYear()} JWC Holdings LLC. All rights reserved.
            </p>
            <p className="body-small text-neutral-500">
              Preparing social workers for ASWB licensing exams since 2024
            </p>
          </div>
          {/* Trademark non-affiliation notice. Required wherever the ASWB mark is
              used in commerce; mirrors the iOS LegalDisclaimerFooter and the Pass
              Plan PDF footer. Legal review 2026-08-25 (P0-4). */}
          <p className="mt-4 text-xs leading-relaxed text-neutral-500 text-center md:text-left">
            ASWB is a registered trademark of the Association of Social Work Boards, which is not
            affiliated with and does not endorse this site.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
